// client/src/components/routing/PrivateRoute.js
import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import MainLayout from '../layout/MainLayout';
import { Loader2 } from 'lucide-react';
import { getUserScope } from '../../utils/permissions';

// Espelha a hierarquia do servidor (permissions.js)
const SCOPE_HIERARCHY = {
    'matriz': 1,
    'franquia': 2,
    'conta': 3,
    'terceiro': 4,
    'cliente': 5
};

// Aceita tanto nomes de scope ('matriz') quanto nomes legados de role ('matriz_admin')
const ROLE_NAME_TO_SCOPE = {
    'matriz_admin': 'matriz',
    'franquia_admin': 'franquia',
    'conta_admin': 'conta',
    'admin': 'conta',
    'profissional': 'conta',
    'gerente': 'conta',
    'user': 'conta'
};

function hasScopeAccess(user, allowedRoles) {
    if (!user || !allowedRoles || allowedRoles.length === 0) return true;

    const userScope = getUserScope(user);
    const userLevel = SCOPE_HIERARCHY[userScope] ?? 99;

    // Permissão total sem restrição de scope
    const permissions = user.permissions || user.role?.permissions || [];
    if (permissions.includes('super_admin:bypass_all_checks')) return true;
    if (permissions.includes('matriz_admin:full_access')) return true;

    const allowedScopes = allowedRoles.map(r => ROLE_NAME_TO_SCOPE[r] || r);

    return allowedScopes.some(allowedScope => {
        const allowedLevel = SCOPE_HIERARCHY[allowedScope] ?? 99;
        // Mesmo scope OU usuário tem nível mais alto (menor número = mais privilégio)
        return userScope === allowedScope || userLevel < allowedLevel;
    });
}

const PrivateRoute = ({ roles, requiredFeature }) => {
    const { isAuthenticated, user, isLoading, scope, logout } = useAuth();
    const location = useLocation();

    // ✅ ACHADO GRAVE: o bloqueio de conta pro painel web (client) vivia só
    // no onSuccess do clique de login (hooks/useAuth.js) — navegação
    // direta, refresh de página, ou qualquer token de conta já presente no
    // localStorage/sessionStorage (de qualquer origem) entrava normalmente,
    // sem passar por aquele handler. Este painel é exclusivo pra
    // matriz/franquia — contas usam o app mobile. Reforçado aqui pra rodar
    // em TODA entrada em rota protegida, não só no clique de login. Em
    // useEffect (não direto no corpo do componente) pra não disparar o
    // side-effect (limpar storage + redirecionar) durante o render.
    useEffect(() => {
        if (isAuthenticated && scope === 'conta') {
            logout();
        }
    }, [isAuthenticated, scope, logout]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader2 className="animate-spin text-indigo-600" size={48} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (scope === 'conta') {
        return null;
    }

    if (user?.requirePasswordChange && location.pathname !== '/force-change-password') {
        return <Navigate to="/force-change-password" replace />;
    }

    if (!user?.requirePasswordChange && location.pathname === '/force-change-password') {
        return <Navigate to="/" replace />;
    }

    if (roles && !hasScopeAccess(user, roles)) {
        return <Navigate to="/acesso-negado" replace />;
    }

    if (requiredFeature) {
        const hasFeature = user?.account?.plano?.features?.[requiredFeature] ?? false;
        if (!hasFeature) {
            return <Navigate to="/assinatura?reason=feature" replace />;
        }
    }

    return (
        <MainLayout>
            <Outlet />
        </MainLayout>
    );
};

export default PrivateRoute;